import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Layout, Menu, Modal, Spin } from "antd";
import Icon, { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import axios from "axios";
import StickyBox from "react-sticky-box";
import SimpleBarReact from "simplebar-react";
import Utils from "../../misc/Utils";
import Enums from "../../misc/Enums";
import {
   useConfigSettings,
   useCurrentSchoolYear,
   useSchoolYearModal,
   useLogout,
   useELibraryLink,
} from "./useSideMenu";
import {
   buildEnrollmentStudentMenu,
   buildEnrollmentParentMenu,
   buildRegistrarMenu,
   buildGuidanceMenu,
   buildStudentMenu,
   buildParentMenu,
   buildStaffMenu,
   buildSudecoMenu,
} from "./menuConfig";
import { SchoolYearModal, ProfileModal, ZoomPickerModal, BraineeLiveClassPickerModal } from "./SideMenuModals";
import { getProfilePictureUrl } from "../profile/profilePictureUrl";
import { hideUnavailableMenuItems, isTvetTenant } from "../../misc/TvetTenant";
import {
   markTenantAssetMissing,
   resolveLoginAssetsSync,
   TENANT_LOGIN_ASSETS,
} from "../../misc/TenantAssets";
import { applyTenantLabelsToMenu } from "../../misc/TenantLabels";
import { getSidebarTheme } from "./sideMenuTheme";
import "./sideMenu.css";

const { Sider } = Layout;

const ROOT_SUBMENU_KEYS = ["sub-1","sub0", "sub1", "sub2", "sub3", "sub4", "sub5", "sub6", "microcredentials"];
const PROFILE_FALLBACK = "/images/brainee_mascot.png";

// Sider skin per tenant: CampusCloud keeps #adccff, a TVET campus gets the legacy #E2EBFF
// (sideMenuTheme.js carries the legacy line references). Widened `...` so future theme keys (e.g.
// a campus-specific accent) only have to be added in one place.
const siderStyle = (theme, collapsed) => ({
   overflow: "auto",
   height: "100vh",
   position: "fixed",
   left: 0,
   top: 0,
   bottom: 0,
   backgroundColor: theme.background,
   ...(collapsed ? {} : { boxShadow: "1px 0px 8px rgba(0, 0, 0, 0.5)" }),
});

const ZoomSvg = () => (
   <svg width="1em" height="1em" viewBox="0 0 192 192" xmlns="http://www.w3.org/2000/svg" fill="none">
      <path
         stroke="#000000"
         strokeLinecap="round"
         strokeLinejoin="round"
         strokeWidth="11.997"
         d="M16.869 60.973v53.832c.048 12.173 10.87 21.965 24.072 21.925h85.406c2.42 0 4.385-1.797 4.385-3.978V78.92c-.064-12.164-10.887-21.965-24.073-21.917H21.237c-2.412 0-4.368 1.79-4.368 3.97zm119.294 21.006 35.27-23.666c3.06-2.332 5.432-1.749 5.432 2.468v72.171c0 4.8-2.9 4.217-5.432 2.468l-35.27-23.618V81.98z"
      />
   </svg>
);

const ZoomIcon = (props) => <Icon component={ZoomSvg} {...props} />;

function SideMenuShared({ variant = "desktop" }) {
   const isSmallScreen = variant === "small";
   const [collapsed, setCollapsed] = useState(true);
   const [openKeys, setOpenKeys] = useState(["1"]);
   const [profileModalVisible, setProfileModalVisible] = useState(false);
   const [zoomPickerVisible, setZoomPickerVisible] = useState(false);
   const [braineeLiveClassPickerVisible, setBraineeLiveClassPickerVisible] = useState(false);
   const [studentLiveClassRoomPickerVisible, setStudentLiveClassRoomPickerVisible] = useState(false);
   const [checkingStudentLiveClassRooms, setCheckingStudentLiveClassRooms] = useState(false);
   const [studentLiveClassRooms, setStudentLiveClassRooms] = useState([]);
   const [joiningStudentLiveClassSectionId, setJoiningStudentLiveClassSectionId] = useState(null);
   const liveClassStatusRequestInFlight = useRef(false);
   const liveClassJoinInFlight = useRef(false);

   const subdomain = localStorage.getItem("lms-subdomain");

   // Sidebar branding (migration plan B-2). The five logo <img> tags used to hardcode the Brainee
   // mark, which is why a TVET campus saw another organisation's logo. The sidebar now shows the
   // very same mark as its login page — {subdomain}/assets/login_logo.png, bundled image
   // /images/1TVETLogo.png (misc/TenantAssets.js) — so the sidebar and the login header never show
   // two different school logos. The bundled fallback is the new Don Bosco One TVET Philippines
   // mark, whose 2.4:1 proportions need `objectFit: "contain"` inside the 220x50 header box (the
   // box sizes below are untouched: only the artwork changes).
   const tvetTenant = isTvetTenant(subdomain);
   // Colour, logo size and header arrangement come from the sidebar skin (sideMenuTheme.js) —
   // #E2EBFF, 220x50, centred: the legacy 1TVET header.
   const sidebarTheme = getSidebarTheme(subdomain);
   const loginAssets = resolveLoginAssetsSync();
   const sidebarLogoFallback = "/images/1TVETLogo.png";
   const sidebarLogoSmallFallback = "/images/1TVETLogo.png";
   const sidebarLogo = tvetTenant ? loginAssets.logo : sidebarLogoFallback;
   const sidebarLogoSmall = tvetTenant ? loginAssets.logo : sidebarLogoSmallFallback;

   // A tenant without an uploaded logo makes S3 answer 403; swap to the bundled image once and
   // remember the miss so the next mount goes straight to the fallback (same as TenantAssets).
   const handleSidebarLogoError = (assetPath, fallback) => (event) => {
      const { currentTarget } = event;

      if (assetPath) {
         markTenantAssetMissing(assetPath);
      }

      if (currentTarget && currentTarget.dataset.tenantFallback !== "1") {
         currentTarget.dataset.tenantFallback = "1";
         currentTarget.src = fallback;
      }
   };

   const userId = Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const otherData = JSON.parse(Utils.getCurrentUserInfo(Enums.UserInfo.otherdata));
   const username = otherData.Username;
   const status = otherData.Status;
   const legacyPicture = otherData.Picture
      ? getProfilePictureUrl(otherData.Picture, subdomain)
      : PROFILE_FALLBACK;
   const initialPicture = getProfilePictureUrl(`userprofiles/${userId}/${userId}.png`, subdomain);
   const [picture, setPicture] = useState(initialPicture);

   const { userCanChangeUsername, userCanChangePassword, hasOA, hasZoom, hasEmergencySheet, hasBraineeConference, hasMicrocredentials } = useConfigSettings();
   const { currentSchoolYear, setCurrentSchoolYear } = useCurrentSchoolYear();
   const schoolYearModal = useSchoolYearModal(currentSchoolYear, setCurrentSchoolYear);
   const handleLogout = useLogout(username);

   const moduleNotAvailable = () =>
      Modal.info({
         title: "Brainee LMS",
         content: "We will let you know once this module is available.",
         centered: true,
      });

   const handleELibraryClick = useELibraryLink(subdomain, moduleNotAvailable);

   const openLiveClassWindow = (url) => {
      const liveClassWindow = window.open(url, "_blank");

      if (liveClassWindow) {
         liveClassWindow.opener = null;
         return;
      }

      Modal.error({
         title: "Brainee Live Class",
         content: "Unable to open live class. Please allow pop-ups and try again.",
         centered: true,
      });
   };

   const getSubdomain = () => {
      const host = window.location.host;
      let currentSubdomain = host.split(".")[0];

      if (currentSubdomain.indexOf("localhost") !== -1) {
         currentSubdomain = "localhost";
      }

      // currentSubdomain = "donboscocanlubang";

      return currentSubdomain;
   };

   const getCsvList = (value) => {
      if (Array.isArray(value)) {
         return value.map((item) => `${item}`.trim()).filter(Boolean);
      }

      return `${value || ""}`
         .split(",")
         .map((item) => item.trim())
         .filter(Boolean);
   };

   const getStudentLiveClassRooms = (studentInfo) => {
      const gradeLevelId = studentInfo?.grade_level;
      const gradeLevelName = studentInfo?.grade_level_name;
      const sectionIds = getCsvList(studentInfo?.section);
      const sectionNames = getCsvList(studentInfo?.section_name);

      return sectionIds.map((sectionId, index) => {
         const sectionName = sectionNames[index] || sectionId;
         const roomName = [gradeLevelName, sectionName].filter(Boolean).join(" - ");

         return {
            gradeLevelId,
            sectionId,
            roomName,
         };
      });
   };

   const normalizeLiveClassStatus = (data, gradeLevelId, sectionId) => {
      const item = Array.isArray(data)
         ? data.find((entry) => `${entry.grade_level_id}` === `${gradeLevelId}` && `${entry.section_id}` === `${sectionId}`)
         : data;

      return {
         isRunning: item?.isRunning === true || item?.isRunning === "true" || item?.running === true || item?.running === "true",
         gradeLevelId: item?.grade_level_id ?? gradeLevelId,
         sectionId: item?.section_id ?? sectionId,
         meetingId: item?.meeting_id ?? item?.meetingId,
      };
   };

   const getLiveClassRoomStatus = async (room) => {
      try {
         const statusResponse = await axios.get(`${process.env.REACT_APP_API_BIGBLUEBUTTON}/ismeetingrunning/picker`, {
            params: {
               subdomain: getSubdomain(),
               grade_level_id: room.gradeLevelId,
               section_id: room.sectionId,
            },
         });

         const status = normalizeLiveClassStatus(statusResponse.data, room.gradeLevelId, room.sectionId);

         return {
            ...room,
            isRunning: status.isRunning,
            meetingId: status.meetingId,
            hasStatusError: false,
         };
      } catch (error) {
         console.log(error.message);
         return {
            ...room,
            isRunning: false,
            hasStatusError: true,
         };
      }
   };

   const getStudentLiveClassRoomsWithStatus = (rooms) =>
      Promise.all(rooms.map(getLiveClassRoomStatus));

   const joinStudentLiveClassRoom = ({ gradeLevelId, sectionId }) => {
      if (liveClassJoinInFlight.current) {
         return;
      }

      liveClassJoinInFlight.current = true;
      setJoiningStudentLiveClassSectionId(sectionId);

      const params = new URLSearchParams({
         subdomain: getSubdomain(),
         joiner_name: Utils.getCurrentUserInfo(Enums.UserInfo.fullname),
         grade_level_id: gradeLevelId,
         section_id: sectionId,
         join_as: "attendee",
      });

      const url = `${process.env.REACT_APP_API_BIGBLUEBUTTON}/joinclass?${params.toString()}`;

      openLiveClassWindow(url);
      window.setTimeout(() => {
         setJoiningStudentLiveClassSectionId(null);
         liveClassJoinInFlight.current = false;
      }, 1000);
   };

   const handleStudentLiveClassRoomSelect = (room) => {
      if (!room.isRunning) {
         return;
      }

      setStudentLiveClassRoomPickerVisible(false);
      joinStudentLiveClassRoom(room);
   };

   const getStudentLiveClassRoomButtonProps = (room) => {
      if (room.hasStatusError) {
         return {
            canJoin: false,
            style: {
               backgroundColor: "#fff7e6",
               borderColor: "#d4a017",
               color: "#d4a017",
            },
         };
      }

      if (room.isRunning) {
         return {
            canJoin: true,
            style: {
               backgroundColor: "#eeffe5",
               borderColor: "#52c41a",
               color: "#52c41a",
            },
         };
      }

      return {
         canJoin: false,
         style: {
            backgroundColor: "#fff1f0",
            borderColor: "#ff4d4f",
            color: "#ff4d4f",
         },
      };
   };

   const handleStudentLiveClassJoin = async () => {
      if (liveClassStatusRequestInFlight.current) {
         return;
      }

      liveClassStatusRequestInFlight.current = true;
      setStudentLiveClassRooms([]);
      setCheckingStudentLiveClassRooms(true);
      setStudentLiveClassRoomPickerVisible(true);

      try {
         const response = await axios.get(`${process.env.REACT_APP_API_MANAGEMENT}/v2/getstudentinfo?id=${userId}`);
         const studentInfo = response.data;
         const gradeLevelId = studentInfo?.grade_level;
         const rooms = getStudentLiveClassRooms(studentInfo);

         if (!gradeLevelId || rooms.length === 0) {
            setStudentLiveClassRoomPickerVisible(false);
            Modal.error({
               title: "Brainee Live Class",
               content: "Unable to join class. Student grade level or section is missing.",
               centered: true,
            });
            return;
         }

         const roomsWithStatus = await getStudentLiveClassRoomsWithStatus(rooms);
         setStudentLiveClassRooms(roomsWithStatus);
      } catch (error) {
         console.log(error.message);
         setStudentLiveClassRoomPickerVisible(false);
         Modal.error({
            title: "Brainee Live Class",
            content: "Unable to load student class information.",
            centered: true,
         });
      } finally {
         liveClassStatusRequestInFlight.current = false;
         setCheckingStudentLiveClassRooms(false);
      }
   };

   useEffect(() => {
      const onResize = () => setCollapsed(true);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
   }, []);

   const onOpenChange = (keys) => {
      const latestKey = keys.find((key) => !openKeys.includes(key));
      if (ROOT_SUBMENU_KEYS.includes(latestKey)) {
         setOpenKeys(latestKey ? [latestKey] : []);
      } else {
         setOpenKeys(keys);
      }
   };

   const getMenuItems = () => {
      if (userRole === "Student" && status === "0") {
         return buildEnrollmentStudentMenu(handleLogout, subdomain);
      }
      if (userRole === "Parent" && status === "0") {
         return buildEnrollmentParentMenu(handleLogout);
      }
      if (["Registrar", "Cashier"].includes(userRole)) {
         return buildRegistrarMenu(handleLogout, userRole, subdomain);
      }
      if (userRole === "Guidance") {
         return buildGuidanceMenu(handleLogout, subdomain);
      }
      if (subdomain === "sudeco") {
         return buildSudecoMenu(userRole, handleLogout);
      }
      if (userRole === "Student") {
         return buildStudentMenu({
            subdomain,
            hasOA,
            hasBraineeConference,
            hasMicrocredentials,
            hasEmergencySheet,
            onUnavailable: moduleNotAvailable,
            onELibraryClick: handleELibraryClick,
            onLiveClassJoin: handleStudentLiveClassJoin,
            onLogout: handleLogout,
         });
      }
      if (userRole === "Parent") {
         return buildParentMenu({
            subdomain,
            hasOA,
            hasEmergencySheet,
            onUnavailable: moduleNotAvailable,
            onLogout: handleLogout,
         });
      }

      return buildStaffMenu({
         userRole,
         subdomain,
         hasZoom,
         hasBraineeConference,
         hasMicrocredentials,
         ZoomIcon,
         onUnavailable: moduleNotAvailable,
         onELibraryClick: handleELibraryClick,
         onLiveClassPickerOpen: () => setBraineeLiveClassPickerVisible(true),
         onZoomPickerOpen: () => setZoomPickerVisible(true),
         onLogout: handleLogout,
      });
   };

   const handleMenuClick = () => {
      setCollapsed(true);
      localStorage.removeItem("mastery-report-search-keys");
      localStorage.removeItem("mastery-assessment-search-keys");
   };

   // Only these roles may open the school-year picker from the header logo. CampusCloud: Super
   // Admin. TVET also lets Admins, as the legacy header did (SideMenuNew.js:613).
   const canChangeSchoolYear = sidebarTheme.schoolYearRoles.includes(userRole);

   const renderProfileImage = () =>
      userRole === "Parent" ? (
         <Link to="/profile">
            <img width={sidebarTheme.avatarSize} height={sidebarTheme.avatarSize} src={picture} alt="Profile" loading="lazy" decoding="async" style={{ borderRadius: "50%" }} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = legacyPicture; }} />
         </Link>
      ) : (
         <img
            width={sidebarTheme.avatarSize}
            height={sidebarTheme.avatarSize}
            src={picture}
            alt="Profile"
            loading="lazy"
            decoding="async"
            style={{ borderRadius: "50%", cursor: "pointer" }}
            onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = legacyPicture; }}
            onClick={() => setProfileModalVisible(true)}
         />
      );

   // Header logo; clicking it opens the school-year picker for the roles the tenant skin allows.
   const renderHeaderLogo = () => (
      <img
         width={sidebarTheme.logo.width}
         height={sidebarTheme.logo.height}
         src={sidebarLogo}
         alt="Don Bosco 1TVET"
         style={{
            ...(canChangeSchoolYear ? { cursor: "pointer" } : {}),
            // The 1TVET mark is 2.4:1 wide; `contain` keeps it undistorted inside the unchanged
            // 220x50 legacy box, the same way the login page draws it (login-panel__logo).
            objectFit: "contain",
         }}
         onClick={canChangeSchoolYear ? schoolYearModal.open : undefined}
         onError={handleSidebarLogoError(tvetTenant ? TENANT_LOGIN_ASSETS.logo : null, sidebarLogoFallback)}
      />
   );

   const renderDesktopHeader = () => (
      <StickyBox style={{ zIndex: 1000000 }}>
         <div
            style={{
               width: "100%",
               padding: "14px 10px 16px 12px",
               background: sidebarTheme.background,
               display: "inline-flex",
               justifyContent: sidebarTheme.headerJustify,
               ...(sidebarTheme.headerJustify === "center" ? { alignItems: "center" } : {}),
            }}
         >
            {collapsed ? (
               sidebarTheme.collapsedShowsAvatar ? (
                  // Legacy TVET rail showed the avatar, not the logo (SideMenuNew.js:559-586).
                  renderProfileImage()
               ) : (
                  <img
                     width={sidebarTheme.collapsedLogo.width}
                     height={sidebarTheme.collapsedLogo.height}
                     src={sidebarLogoSmall}
                     alt="Logo"
                     style={{ objectFit: "contain" }}
                     onError={handleSidebarLogoError(tvetTenant ? TENANT_LOGIN_ASSETS.logo : null, sidebarLogoSmallFallback)}
                  />
               )
            ) : sidebarTheme.avatarFirst ? (
               <>
                  {renderProfileImage()}
                  {renderHeaderLogo()}
               </>
            ) : (
               <>
                  {renderHeaderLogo()}
                  {renderProfileImage()}
               </>
            )}
         </div>
      </StickyBox>
   );

   const renderSmallTopBar = () => (
      <div className="menu-icon-small" style={{ backgroundColor: sidebarTheme.background }}>
         <div style={{ paddingTop: 10, top: -5, paddingLeft: 14 }}>
            {React.createElement(!collapsed ? MenuFoldOutlined : MenuUnfoldOutlined, {
               className: "trigger",
               onClick: () => setCollapsed(!collapsed),
               style: { color: sidebarTheme.accent },
            })}
         </div>
         <div style={{ marginTop: 7, width: "100%", display: "flex", justifyContent: "space-between" }}>
            {renderHeaderLogo()}
            {renderProfileImage()}
         </div>
      </div>
   );

   const renderSmallDrawerHeader = () => (
      <StickyBox style={{ zIndex: 1000000, width: "100%" }}>
         <div
            style={{
               width: "100%",
               paddingTop: 7,
               paddingBottom: 8,
               background: sidebarTheme.background,
               display: "inline-flex",
            }}
         >
            {React.createElement(!collapsed ? MenuFoldOutlined : MenuUnfoldOutlined, {
               className: "trigger",
               onClick: () => setCollapsed(!collapsed),
               style: { color: sidebarTheme.accent, fontSize: 30, paddingTop: 10, paddingLeft: 15 },
            })}
            {collapsed ? (
               sidebarTheme.collapsedShowsAvatar ? (
                  renderProfileImage()
               ) : (
                  <img
                     width={sidebarTheme.collapsedLogo.width}
                     height={sidebarTheme.collapsedLogo.height}
                     src={sidebarLogoSmall}
                     alt="Logo"
                     style={{ objectFit: "contain" }}
                     onError={handleSidebarLogoError(tvetTenant ? TENANT_LOGIN_ASSETS.logo : null, sidebarLogoSmallFallback)}
                  />
               )
            ) : (
               renderHeaderLogo()
            )}
         </div>
      </StickyBox>
   );

   // Two passes over the same menu, both no-ops on a CampusCloud tenant: drop the entries whose
   // tables do not exist (TvetTenant.js), then give the survivors TVET wording (TenantLabels.js).
   const tenantMenuItems = () =>
      hideUnavailableMenuItems(applyTenantLabelsToMenu(getMenuItems(), subdomain), subdomain);

   const renderMenu = () => (
      <SimpleBarReact style={{ height: "calc(100vh - 80px)" }}>
         <Menu
            className="sidebar-menu"
            style={{ background: sidebarTheme.background }}
            mode="inline"
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            defaultSelectedKeys={["1"]}
            items={tenantMenuItems()}
            onClick={handleMenuClick}
         />
      </SimpleBarReact>
   );

   const renderDesktopMenu = () => (
      <nav className="menu-area">
         <Layout hasSider>
            <Sider
               collapsible
               collapsed={collapsed}
               onMouseEnter={() => setCollapsed(false)}
               onMouseLeave={() => setCollapsed(true)}
               style={siderStyle(sidebarTheme, collapsed)}
               width={sidebarTheme.width}
               trigger={null}
            >
               {renderDesktopHeader()}
               {renderMenu()}
            </Sider>
         </Layout>
      </nav>
   );

   const renderSmallMenu = () => (
      <>
         {renderSmallTopBar()}
         <div className={!collapsed ? "menu-area2" : "menu-area2 for-menu-scroll"}>
            <Layout hasSider>
               <Sider
                  collapsible
                  collapsed={collapsed}
                  onCollapse={(value) => setCollapsed(value)}
                  style={siderStyle(sidebarTheme, false)}
                  width="280px"
                  collapsedWidth="0"
                  trigger={null}
               >
                  {renderSmallDrawerHeader()}
                  {renderMenu()}
               </Sider>
            </Layout>
         </div>
      </>
   );

   return (
      <>
         {isSmallScreen ? renderSmallMenu() : renderDesktopMenu()}

         <SchoolYearModal
            open={schoolYearModal.visible}
            updating={schoolYearModal.updating}
            schoolYearList={schoolYearModal.schoolYearList}
            currentSchoolYear={schoolYearModal.currentSchoolYear}
            onSchoolYearChange={schoolYearModal.setSchoolYear}
            onOk={schoolYearModal.confirm}
            onCancel={schoolYearModal.close}
         />

         <ProfileModal
            open={profileModalVisible}
            userRole={userRole}
            userId={userId}
            canChangeUsername={userCanChangeUsername}
            canChangePassword={userCanChangePassword}
            onPictureUploaded={(key, previewUrl) => {
               setPicture(previewUrl || getProfilePictureUrl(key, subdomain, Date.now()));
            }}
            onCancel={() => setProfileModalVisible(false)}
         />

         <ZoomPickerModal
            open={zoomPickerVisible}
            onClose={() => setZoomPickerVisible(false)}
         />

         <BraineeLiveClassPickerModal
            open={braineeLiveClassPickerVisible}
            onClose={() => setBraineeLiveClassPickerVisible(false)}
         />

         <Modal
            title="Select Brainee Live Class Room"
            centered
            destroyOnClose
            open={studentLiveClassRoomPickerVisible}
            onCancel={() => setStudentLiveClassRoomPickerVisible(false)}
            footer={[
               <Button
                  key="cancel"
                  type="default"
                  shape="round"
                  onClick={() => setStudentLiveClassRoomPickerVisible(false)}
               >
                  Cancel
               </Button>,
            ]}
            width={420}
            style={{ minWidth: 300 }}
         >
            {checkingStudentLiveClassRooms ? (
               <div style={{ padding: "24px 0", textAlign: "center" }}>
                  <Spin />
                  <div style={{ marginTop: 12, color: "#5896D1" }}>
                     Checking live class rooms...
                  </div>
               </div>
            ) : (
               <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {studentLiveClassRooms.map((room) => {
                     const buttonProps = getStudentLiveClassRoomButtonProps(room);
                     const isJoiningThisRoom = joiningStudentLiveClassSectionId === room.sectionId;

                     return (
                        <Button
                           key={`${room.gradeLevelId}-${room.sectionId}`}
                           block
                           shape="round"
                           loading={isJoiningThisRoom}
                           disabled={joiningStudentLiveClassSectionId !== null}
                           onClick={() => handleStudentLiveClassRoomSelect(room)}
                           style={{
                              ...buttonProps.style,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              opacity: joiningStudentLiveClassSectionId !== null && !isJoiningThisRoom ? 0.45 : 1,
                              cursor: buttonProps.canJoin ? "pointer" : "not-allowed",
                           }}
                        >
                           <span
                              style={{
                                 display: "block",
                                 width: "100%",
                                 overflow: "hidden",
                                 textOverflow: "ellipsis",
                                 whiteSpace: "nowrap",
                              }}
                              title={room.roomName}
                           >
                              {room.roomName}
                           </span>
                        </Button>
                     );
                  })}
               </div>
            )}
         </Modal>
      </>
   );
}

export default SideMenuShared;
